import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { BusinessUserProfileForm } from "@tootsweet/model/entities/BusinessUserProfileForm";
import LoginV2API from "../api/api";
import JetpackLoader from "../../../components/Loaders/JetpackLoader/JetpackLoader";
import { useUrlQuery } from "../../../utils/hooks/useUrlQuery";
import { tt } from "../../../utils/i18n/i18n";
import BaseJSBackendResponse from "../../../model/responsemodel/BaseJSBackendResponse";
import getErrorMessage from "../../../utils/error_handling/getErrorMessage";
import ReturnCodes from "../../../utils/error_handling/ReturnCodes";
import KC_Heading0 from "../../../designsystem/typography/KC-Heading/KC_Heading0";
import TwemojiWrapper from "../../../components/TwemojiWrapper/TwemojiWrapper";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_Input from "../../../designsystem/KC_FormInput/KC_Input";
import KC_Heading1 from "../../../designsystem/typography/KC-Heading/KC_Heading1";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../designsystem/KC_Button/KC_Button";
import { SIGNUP } from "../../../utils/routes/routes";
import SignUpLayout from "./SignUpLayout";
import { useSubscriptionPlansSelector } from "../../../redux/actions/app";
import "../styles.scss";

export const validationEmail = Yup.object({
  email: Yup.string()
    .email(tt("signup_invalid_email"))
    .required(tt("signup_email_required"))
});

export default function EmailVerification() {
  const history = useHistory();
  const location = useLocation<BusinessUserProfileForm>();
  const query = useUrlQuery();
  const plans = useSubscriptionPlansSelector();

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const planId = useMemo(() => {
    const queryPlanId = query.get("planId");

    if (!queryPlanId && plans?.length) {
      const defaultPlan = plans.find((p) => p.isDefault);
      return defaultPlan?._id || null;
    }

    return queryPlanId;
  }, [query, plans]);

  const subscriptionPromoCode2 = useMemo(() => {
    return query.get("pc") ?? "";
  }, [query]);

  const isLogin = useMemo(() => {
    return !!query.get("login");
  }, [query]);

  const handleSubmit = async (values: { email: string }) => {
    setIsLoading(true);
    try {
      const locale = navigator.language || "en-US";
      await LoginV2API.generateCustomer(
        values.email,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        subscriptionPromoCode2,
        locale,
        location.state,
        planId
      );
      const search = new URLSearchParams(window.location.search);
      search.set("email", values.email);
      history.push(`/validate?${search.toString()}`);
    } catch (e) {
      console.error(e);

      //@ts-ignore
      const payload = e?.response?.data as BaseJSBackendResponse<null>;

      if (!!payload) {
        setError(getErrorMessage(payload?.code || ReturnCodes.Generic));
      } else {
        setError(tt("signup_generic_error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <JetpackLoader loading={isLoading} isFullScreen={true} />;
  }

  if (error) {
    return (
      <SignUpLayout>
        <div className="d-flex flex-column align-items-center st-gap">
          <TwemojiWrapper isBigText={true}>⚠</TwemojiWrapper>
          <KC_Heading0 text={error} className="text-center" />
        </div>
      </SignUpLayout>
    );
  }

  return (
    <SignUpLayout>
      <div className="d-flex flex-column align-items-center sign-up-text">
        {isLogin && <KC_Heading1 text={tt("log_in")} className="text-center" />}
        {!isLogin && (
          <KC_Heading1
            text={tt("signup_heading_email")}
            className="text-center"
          />
        )}
      </div>
      <Formik
        initialValues={{ email: "" }}
        validationSchema={validationEmail}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form className="signup-form">
              <div className="w-100">
                <KC_Input
                  id="signup-email"
                  required={true}
                  htmlFor="email"
                  placeholder={tt("signup_email_placeholder")}
                  setFieldValue={setFieldValue}
                  type="text"
                  value={values.email}
                />
              </div>
              <KC_Button
                isPrimary
                id="submit-btn"
                text={
                  isLogin
                    ? tt("signup_login_button")
                    : subscriptionPromoCode2
                      ? tt("signup_create_account")
                      : tt("signup_create_free_account")
                }
                type={KC_ButtonType.Submit}
                size={KC_ButtonSize.Giant}
                className="d-flex"
              />
              {isLogin && (
                <KC_UIText
                  weight="reg"
                  text={tt("signup_create_account_link")}
                  className="underline cursor-pointer"
                  onClick={() => {
                    history.push(SIGNUP);
                  }}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </SignUpLayout>
  );
}
